import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';
import { OPTIMISTIC_OPERATION_KEY } from '../optimistic-handles';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export const useDuplicateOperation = ({
  operation,
  testContentId,
  parentContext,
}: {
  operation?: SelectedOperation;
  testContentId: string;
  parentContext?: OperationParentContext;
}) => {
  const trpcUtils = api.useUtils();

  return api.operation.duplicateOperation.useMutation<TestContentMutationContext>({
    onSuccess: () => handleMutationSuccess({ trpcUtils, testContentId, parentContext }),
    onError: (_error, _variables, context) =>
      handleMutationError({ trpcUtils, testContentId, message: 'Error unable to duplicate action', context }),
    onMutate: async ({ operationId }) => {
      await cancelTestContentQueries({ trpcUtils, testContentId, parentContext });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const targetStep = prevData.steps.find((step) => step.id === operation?.testStep?.id);
      if (!targetStep) {
        return { prevData };
      }

      const targetOperation = targetStep.operations?.find((op) => op.id === operationId);
      if (!targetOperation) {
        return { prevData };
      }

      const newData = {
        ...prevData,
        steps: prevData.steps.map((step) =>
          step.id === targetStep.id && step.operations ?
            {
              ...step,
              operations: [
                ...step.operations.map((op) =>
                  op.order > targetOperation.order ? { ...op, order: op.order + 1 } : op,
                ),
                {
                  ...targetOperation,
                  id: `${OPTIMISTIC_OPERATION_KEY}-${targetOperation.id}-${Date.now()}`,
                  order: targetOperation.order + 1,
                },
              ].sort((a, b) => a.order - b.order),
            }
          : step,
        ),
      };

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });
};
